import React, { useEffect, createContext, useContext } from 'react';
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';

import { Navbar } from './components/layout/Navbar';
import { HeroSection } from './components/layout/HeroSection';
import { MarketGrid } from './components/dashboard/MarketGrid';
import { SimulatorView } from './components/dashboard/SimulatorView';
import { Portfolio } from './components/dashboard/Portfolio';
import { Leaderboard } from './components/dashboard/Leaderboard';
import { TelegramSection } from './components/sections/TelegramSection';
import { RoadmapSection } from './components/sections/RoadmapSection';
import { LobsterPulse } from './components/dashboard/LobsterPulse';
import { BetaSimulation } from './components/pages/BetaSimulation';
import { HumanControlInterface } from './components/dashboard/HumanControlInterface';
import { AllMarketsPage } from './components/pages/AllMarketsPage';
import { AgentAnalysisPage } from './components/pages/AgentAnalysisPage';
import TradingPage from './components/pages/TradingPage';
import { AgentOnboardingPage } from './components/pages/AgentOnboardingPage';
import { AgentDashboard } from './components/pages/AgentDashboard';
import { AgentProvider } from './contexts/AgentContext';
import { PaperTradingProvider } from './contexts/PaperTradingContext';

import { Footer } from './components/layout/Footer';
import authService from './services/authService';

// Wallet Connect Context (Coming Soon - Mocked)
const WalletContext = createContext({
    isConnected: false,
    address: null,
    openConnectModal: () => { },
});

export const useWalletMock = () => useContext(WalletContext);

const queryClient = new QueryClient();

// Main Landing Page Component
const LandingPage = ({ onStartTrading }) => {
    return (
        <div className="min-h-screen bg-background text-white selection:bg-primary/30 relative overflow-hidden font-sans">
            {/* Top Red Border */}
            <div className="fixed top-0 left-0 right-0 h-[2px] bg-primary z-[60]" />

            {/* Side Glows */}
            <div className="fixed left-[-20%] top-1/4 w-[40%] h-[50%] bg-accent/20 blur-[120px] pointer-events-none z-0" />
            <div className="fixed right-[-20%] top-1/4 w-[40%] h-[50%] bg-accent/20 blur-[120px] pointer-events-none z-0" />

            <Navbar onConnect={onStartTrading} />

            <main className="relative z-10">
                <HeroSection onGetStarted={onStartTrading} />
                <div className="container mx-auto px-4 -mt-10 mb-20 relative z-20">
                    <div className="max-w-4xl mx-auto">
                        {/* LobsterPulse removed as per user request */}
                    </div>
                </div>
                <MarketGrid />
                <TelegramSection />
                <RoadmapSection />
            </main>

            <Footer />
        </div>
    );
};

// Routing Wrapper
function AppContent() {
    const navigate = useNavigate();

    const handleStartTrading = () => {
        // Navigate directly to simulator (wallet connect coming soon)
        navigate('/simulator');
    };

    return (
        <Routes>
            <Route path="/" element={<LandingPage onStartTrading={handleStartTrading} />} />
            <Route path="/simulator" element={<SimulatorView onBack={() => navigate('/')} />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/beta-simulation" element={<BetaSimulation />} />
            {/* <Route path="/human-control" element={<HumanControlInterface onBack={() => navigate('/')} />} /> */}
            <Route path="/markets" element={<AllMarketsPage />} />
            <Route path="/agent-analysis" element={<AgentAnalysisPage />} />
            <Route path="/trade/:marketId" element={<TradingPage />} />
            <Route path="/agent-onboarding" element={<AgentOnboardingPage />} />
            <Route path="/agent-dashboard" element={<AgentDashboard />} />
        </Routes>
    );
}

const App = () => {
    return (
        <WalletContext.Provider value={{ isConnected: false, address: null, openConnectModal: () => { } }}>
            <QueryClientProvider client={queryClient}>
                <AgentProvider>
                    <PaperTradingProvider>
                        <Router>
                            <AuthWrapper>
                                <AppContent />
                            </AuthWrapper>
                        </Router>
                    </PaperTradingProvider>
                </AgentProvider>
            </QueryClientProvider>
        </WalletContext.Provider>
    );
};


// Wrapper to handle auth side effects
const AuthWrapper = ({ children }) => {
    useEffect(() => {
        // Monitor Firebase Auth changes (Google Login)
        const unsubscribe = authService.subscribeToAuthChanges(async (user) => {
            if (user) {
                const token = await authService.getAuthToken();
                localStorage.setItem('token', token);
                localStorage.setItem('userEmail', user.email);
                localStorage.setItem('userName', user.displayName);
                localStorage.setItem('userPhoto', user.photoURL);
            }
        });

        return () => unsubscribe();
    }, []);

    return children;
};

export default App;
